import { useMemo, useState } from 'react'
import { NetworkStatus } from '@apollo/client'
import useInfiniteScroll from 'react-infinite-scroll-hook'

import { useDebounce } from 'hooks'
import { useTerritoryQueries } from 'hooks-queries/territory'
import { PanelStore } from 'stores'
import getCustomTopicFormattedUploadDate from 'utils/getCustomTopicFormattedUploadDate'
import getSavedSearchURL from 'utils/getSavedSearchURL'

import { GET_OWNED_TOPICS_LOG, PAGINATION_LIMIT } from './owned-topics-tab.consts'
import type { IOwnedTopicsTab } from './owned-topics-tab.types'

export const useOwnedTopicsTabModel = (): IOwnedTopicsTab => {
  const [searchFilter, setSearchFilter] = useState('')

  const workspaceId = Object(PanelStore?.selectedPanel)?.id

  const { data, error, fetchMore, refetch, networkStatus } = useTerritoryQueries({
    queryName: 'getOwnedTopics',
    queryVariables: {
      pagination: {
        limit: PAGINATION_LIMIT,
      },
    },
    errorLog: GET_OWNED_TOPICS_LOG,
  })

  const tableData = useMemo(() => {
    return data?.listSavedPostSearchesPaginated?.entities.map(entity => {
      const filtersSearchParams = new URLSearchParams(entity?.filters)
      const uploadDateParam = filtersSearchParams.get('uploadDate')
      const uploadDate = getCustomTopicFormattedUploadDate(uploadDateParam)

      return {
        ...entity,
        checkbox: entity?.id,
        topic: {
          name: entity?.name,
          query: entity?.query,
        },
        createdBy: {
          firstName: entity?.createdBy?.firstName,
          createdAt: entity?.createdAt,
        },
        uploadDate: uploadDate.label,
        timeRange: uploadDate.timeRange,
        id: entity?.id,
        filters: entity?.filters,
        href: getSavedSearchURL(
          {
            id: entity?.id,
            filters: entity?.filters,
          },
          workspaceId
        ),
      }
    })
  }, [data, workspaceId])

  const totalVideosLoaded = data?.listSavedPostSearchesPaginated?.entities?.length
  const hasMore = totalVideosLoaded < data?.listSavedPostSearchesPaginated?.rows
  const isLoading = [NetworkStatus.loading, NetworkStatus.refetch, NetworkStatus.setVariables].includes(networkStatus)
  const isFetchingMore = networkStatus === NetworkStatus.fetchMore

  const totalPagination = data?.listSavedPostSearchesPaginated?.rows

  const loadMore = async () => {
    if (NetworkStatus.fetchMore === networkStatus || !hasMore) return

    try {
      await fetchMore({
        variables: {
          pagination: {
            offset: totalVideosLoaded,
            limit: PAGINATION_LIMIT,
          },
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult.listSavedPostSearchesPaginated?.entities?.length) {
            return prev
          }

          const mergedResult = {
            listSavedPostSearchesPaginated: {
              ...prev.listSavedPostSearchesPaginated,
              entities: [
                ...prev.listSavedPostSearchesPaginated.entities,
                ...fetchMoreResult.listSavedPostSearchesPaginated.entities,
              ],
            },
          }

          return mergedResult
        },
      })
    } catch (error) {
      console.error('paginatedPosts @loadmore >>>>>', error)
    }
  }

  const [sentryRef] = useInfiniteScroll({
    loading: isLoading || NetworkStatus.fetchMore === networkStatus,
    hasNextPage: hasMore,
    onLoadMore: loadMore,
    disabled: !!error,
  })

  const handleSearch = searchValue => {
    refetch({
      search: {
        term: searchValue,
      },
    })
  }

  const { debouncedAction: debouncedHandleSearch, cancelDebouncedAction } = useDebounce(handleSearch)

  const handleChange = value => {
    setSearchFilter(value)
    debouncedHandleSearch(value)
  }

  const handleSubmit = e => {
    e?.preventDefault()
    handleSearch(searchFilter)
    cancelDebouncedAction()
  }

  return {
    tableData,
    handleChange,
    handleSubmit,
    sentryRef,
    isLoading,
    isFetchingMore,
    hasMore,
    error: Boolean(error),
    refetch,
    searchFilter,
    totalPagination,
  }
}
